import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { api } from "@/lib/api";

const RANGES = [
  { value: 7, label: "7 días" },
  { value: 30, label: "30 días" },
  { value: 90, label: "90 días" },
];

const SERIES = [
  { key: "leads", label: "Leads", color: "#1B3A6B" },
  { key: "call_intent", label: "Botón llamada (móvil)", color: "#E6217A" },
  { key: "call_now", label: "Llamada iniciada", color: "#8A057D" },
  { key: "booking_online", label: "Reservar online", color: "#0E9F6E" },
  { key: "dossier_pdf", label: "Dossier PDF", color: "#C2821B" },
];

function fmtDay(iso) {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

export default function ActivityChart() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState({});

  useEffect(() => {
    api
      .get("/stats/timeseries", { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [days]);

  const toggle = (key) => setHidden((h) => ({ ...h, [key]: !h[key] }));

  return (
    <section className="card-soft p-5 md:p-6 mb-10" data-testid="activity-chart">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-brand-primary">
            Evolución diaria
          </h2>
          <p className="text-sm text-brand-muted mt-1">
            Leads y clics en los botones clave, día a día. Útil para ver el efecto de cada campaña.
          </p>
        </div>
        <div className="flex gap-1 bg-brand-surface/70 rounded-full p-1">
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setDays(r.value)}
              data-testid={`chart-range-${r.value}`}
              className={`px-3.5 py-1.5 text-xs rounded-full transition-colors ${
                days === r.value
                  ? "bg-white text-brand-primary font-medium shadow-sm"
                  : "text-brand-muted hover:text-brand-primary"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {data?.totals && (
        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5">
          {SERIES.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => toggle(s.key)}
              data-testid={`chart-toggle-${s.key}`}
              className={`flex items-baseline gap-2 text-left transition-opacity ${
                hidden[s.key] ? "opacity-35" : ""
              }`}
            >
              <span className="h-2.5 w-2.5 rounded-full mt-1.5" style={{ background: s.color }} />
              <span className="text-xs text-brand-muted">{s.label}</span>
              <span className="font-outfit font-semibold text-brand-primary tabular-nums">
                {data.totals[s.key] ?? 0}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="h-72 mt-6 -ml-2" data-testid="activity-chart-canvas">
        {data?.series && (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.series} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#E7E3DC" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={fmtDay}
                tick={{ fontSize: 11, fill: "#8A8579" }}
                interval="preserveStartEnd"
                minTickGap={24}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: "#8A8579" }}
                axisLine={false}
                tickLine={false}
                width={32}
              />
              <Tooltip
                labelFormatter={(v) => fmtDay(v)}
                contentStyle={{
                  borderRadius: 12,
                  border: "1px solid #E7E3DC",
                  fontSize: 12,
                  fontFamily: "Manrope, sans-serif",
                }}
              />
              <Legend wrapperStyle={{ display: "none" }} />
              {SERIES.filter((s) => !hidden[s.key]).map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}
